import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../services/redux/types";
import { TResponseApiRequestBody } from "../../../services/api/instances/responseApi/types/request";
import { AGENT_REF, INITIAL_DOCTYPE } from "../constants";
import { generateId } from "../../../shared/utils/generateId";
import {
  deleteThreadWithProxy,
  getMessagesFromThreadWithProxy,
  postMessageToResponseWithProxy,
} from "../../../services/api/instances/responseApi/api";
import { OutputMessageType } from "../../../services/api/instances/responseApi/types/common";
import { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { clearMessagesAction, setSpaValue } from "./slice";
import { EditorValue } from "../../../services/api/instances/editorApi/types";
import { getSpaUpdatedValue } from "../helpers/getSpaUpdatedValue";
import { getText } from "../../../services/api/instances/assistantsApi/methods";
import axios from "axios";
import {
  mergedDialogMessages,
  transformMessagesObject,
} from "../../Dialog/helpers";
import { extractHtmlCode } from "../helpers/extractHtmlCode/extractHtmlCode";
import i18n from "../../../services/i18n";
import { getError } from "../../../services/lib/getError";

export const postMessageWithResponsesAction = createAsyncThunk(
  "editor/postMessageWithResponses",
  async (
    { message, id }: { message: string; id: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const { spaValue, messages } = (getState() as RootState).editorState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .ai_agents_spa;
      const selectedObject = data.entities[selectedId as string];
      if (!spaValue) return;
      const { thread_id, html_content } = spaValue.spa_object;
      const threadId =
        thread_id && thread_id !== "" ? thread_id : `thread_${generateId()}`;
      const userMessage = thread_id
        ? message
        : `${message}\n\n<security>${html_content}</security>`;
      const postObject: TResponseApiRequestBody = {
        input: userMessage,
        model: null,
        metadata: {
          assistant_ref: AGENT_REF,
          thread_ref: threadId,
        },
      };
      const systemResponse = await postMessageToResponseWithProxy(postObject);
      const systemMessage = systemResponse?.output.at(-1) as OutputMessageType;
      const messageContent = extractHtmlCode(
        systemMessage.content?.at(-1)?.text as string,
      );

      const newMessages: ChatCompletionsMessageObject[] = [
        ...messages,
        {
          role: "assistant",
          content: messageContent.agent_message,
        },
      ];

      if (
        (!thread_id && selectedId) ||
        (messageContent?.html_content !== "" && selectedId)
      ) {
        const newValue = getSpaUpdatedValue(spaValue, {
          html_content: messageContent?.html_content
            ? messageContent.html_content
            : spaValue?.spa_object?.html_content,
          thread_id: threadId,
          metadata: {
            isLegacyObject: false,
          },
        });
        dispatch(
          editStorageObject({
            id: selectedId as string,
            name: selectedObject?.friendly_name,
            value: newValue,
            service: "ai_agents_spa",
          }),
        );
        dispatch(setSpaValue(newValue));
      }

      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getEditorMessagesAction = createAsyncThunk(
  "editor/getEditorMessages",
  async (threadRef: string, { getState, rejectWithValue }) => {
    try {
      const { messages } = (getState() as RootState).editorState;
      const responseMessages = await getMessagesFromThreadWithProxy(threadRef);
      const receivedMessages = transformMessagesObject(responseMessages);
      const newMessages = mergedDialogMessages(messages, receivedMessages);
      const processedMessages = newMessages.map((message) => {
        if (message.role === "assistant") {
          const messageContent = extractHtmlCode(
            (message?.content as string) ?? "",
          );
          return {
            ...message,
            content: messageContent.agent_message,
          };
        }
        return message;
      });
      return processedMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteEditorThreadAction = createAsyncThunk(
  "editor/deleteEditorThread",
  async (thread_ref: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { spaValue } = (getState() as RootState).editorState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .ai_agents_spa;
      if (!selectedId || !spaValue?.spa_object) return;
      const selectedObject = data.entities[selectedId];
      if (!selectedObject) return;
      dispatch(clearMessagesAction());

      const newValue: EditorValue = getSpaUpdatedValue(spaValue, {
        html_content: INITIAL_DOCTYPE.replace(
          "{{HEADING}}",
          i18n.t("editor.initial_html_h1"),
        ).replace("{{SUBHEADING}}", i18n.t("editor.initial_html_h2")),
        thread_id: "",
      });

      dispatch(setSpaValue(newValue));

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: newValue,
          service: "ai_agents_spa",
        }),
      );

      return await deleteThreadWithProxy(thread_ref);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
export const uploadFileToEditorDialogAction = createAsyncThunk(
  "editor/uploadFileToEditorDialog",
  async (file: File, { signal, rejectWithValue }) => {
    try {
      return await getText(file, signal);
    } catch (e) {
      if (
        signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
